import { z } from 'zod';
import { assertTypes, type NoNewFields } from '../primitives';

/**
 * Feature flags: a yes/no OrderHub controls per venue (restaurant).
 *
 * - Storage: the `featureFlags` table holds one row per venue per flag that is on. A row existing
 *   means on; turning a flag off deletes the row. No default-on state.
 * - Who: super admins turn any flag on or off (`featureFlags.save`), on the venue's Feature flags
 *   settings page in admin-web. A venue's products follow its plan. Venue staff turn only their
 *   plan's features on or off (`restaurantPlans.saveFeatureFlags`), on the Features settings page,
 *   and never see the rest. Plans: `restaurantPlanSchema.ts`.
 * - Type: lives here only, never in the data. The type decides who turns a flag on or off.
 *   - `product`: what a venue buys whole (POS, online ordering). It follows the venue's plan; venue
 *     staff never turn it on or off. With every product off, a venue keeps only its back office:
 *     its details, staff, menu and past orders.
 *   - `feature`: a capability OrderHub sells inside a plan. Venue staff turn their plan's features
 *     on or off. Whether a feature costs extra is the plan's business (included, or turned on by
 *     OrderHub on top), never the flag's.
 *   - `experimental`: a temporary rollout, reviewed by hand. It graduates by editing its type, or
 *     is deleted (it reached every venue, or was abandoned): delete the key and its checks, then
 *     its rows.
 *   - `internal`: OrderHub-only (support, diagnostics). Never sold or shown to venues.
 *   - Only products and features go in plans.
 * - Requires: the product a flag only works with (`requires`), or `null`. Only a product can be
 *   required; the compiler rejects any other key. While the product is off, the flag is off too,
 *   whatever its own row says, and its row is kept. The product is part of the dependency, not the
 *   name: `loyalty-card`, never `online-ordering-loyalty-card`.
 * - Off means absent everywhere, super admins included: UI hidden, queries return the module's
 *   "nothing set up" value, mutations throw 403 (never 401, which unlinks a POS), jobs skip. The
 *   module's data is kept and comes back when the flag is turned on again. Except
 *   `menu-item-stock`: turning it off deletes the venue's stock counts, since nothing counts sales
 *   while it's off (`saveFeatureFlags`).
 * - Settings (how a venue configures a feature) live in the feature's own module table, never on
 *   `restaurants` and never in a central settings table.
 *
 * Gating a module:
 * - Server reads: while off, return the module's "nothing set up" value (`isFeatureFlagEnabled`).
 *   A module read from several places checks in one reader they all share, e.g.
 *   `getActivePosButtonColors` in `posButtonColors/domain.ts`.
 * - Server writes: the tenancy guard, then `assertFeatureFlagEnabledX`.
 * - Clients: `useFeatureFlagEnabled(restaurantId, key)` (admin-web), `useFeatureFlagEnabled(key)`
 *   (admin-native, the signed-in user's venue), `isFeatureFlagEnabled(data, key)` (pos-native, from
 *   the `getData` payload). Skip module queries while off. Where one read needs both, the use-case
 *   envelope carries `isEnabled`.
 * - Consumer apps never receive the flag list; they get each module's derived value.
 *
 * Adding a flag: add its key to `FEATURE_FLAG_KEYS` and its entry to `FEATURE_FLAGS`. Keys are
 * kebab-case capability names, region-qualified when the capability only works in one region
 * (`integrated-eftpos-australia`). Stored keys are plain strings, so a POS bundle that predates a
 * key still decodes a payload holding it.
 */
// TODO: Products: gate POS (device auth, iPad linking) and online ordering (online status, order
// submission) on their flags, and make `isFeatureFlagEnabled` honour `requires`. Backfill both
// products for every venue that uses them first, or every feature that requires one turns off.
export const FEATURE_FLAG_KEYS = [
  'pos',
  'online-ordering',
  'pos-button-colors',
  'pause-online-orders',
  'support-chat',
  'debug-tools',
  'print-job-logging',
  'integrated-eftpos-australia',
  'custom-domain',
  'square-integration',
  'menu-item-stock',
  'mcp-server',
  'voice-chat',
  'ai-phone-ordering',
] as const;
export const zFeatureFlagKey = z.literal(FEATURE_FLAG_KEYS);
export type FeatureFlagKey = z.output<typeof zFeatureFlagKey>;

/** In the order super admins see them. */
export const FEATURE_FLAG_TYPES = ['product', 'feature', 'internal', 'experimental'] as const;
export const zFeatureFlagType = z.literal(FEATURE_FLAG_TYPES);
export type FeatureFlagType = z.output<typeof zFeatureFlagType>;

/** How super admins see each type. */
export const FEATURE_FLAG_TYPE_LABELS: Record<FeatureFlagType, string> = {
  product: 'Product',
  feature: 'Feature',
  internal: 'Internal',
  experimental: 'Experimental',
};

/**
 * Every flag's type, the product it requires and the copy super admins see. Venue staff see the
 * copy of their plan's products and features too, on the Features page, so write it for cafe
 * staff.
 * - `turnOffWarning`: what turning the flag off does that staff can't undo, shown before a save
 *   turns it off. `null` for a flag that only hides its feature while off.
 */
export const FEATURE_FLAGS = {
  pos: {
    type: 'product',
    requires: null,
    label: 'POS',
    description: 'Take orders and payments on iPads at the counter.',
    turnOffWarning: null,
  },
  'online-ordering': {
    type: 'product',
    requires: null,
    label: 'Online ordering',
    description: 'Customers order ahead from your website and app.',
    turnOffWarning: null,
  },
  'pos-button-colors': {
    type: 'feature',
    requires: 'pos',
    label: 'POS button colours',
    description: 'Pick a colour for each item and modifier button on the POS.',
    turnOffWarning: null,
  },
  'pause-online-orders': {
    type: 'experimental',
    requires: 'online-ordering',
    label: 'Pause online orders',
    description: 'The Store tab in the admin app: online status, pause and resume online orders.',
    turnOffWarning: null,
  },
  'support-chat': {
    type: 'experimental',
    requires: null,
    label: 'Support chat',
    description: 'Chat with OrderHub support from the admin website, and in the admin app.',
    turnOffWarning: null,
  },
  'debug-tools': {
    type: 'internal',
    requires: null,
    label: 'Debug tools',
    description: 'PostHog test buttons and the Linkly environment switch on the POS.',
    turnOffWarning: null,
  },
  'print-job-logging': {
    type: 'internal',
    requires: 'pos',
    label: 'Print job logging',
    description: 'The POS uploads each print job so it can be inspected remotely.',
    turnOffWarning: null,
  },
  'integrated-eftpos-australia': {
    type: 'feature',
    requires: 'pos',
    label: 'Integrated EFTPOS (Australia)',
    description: 'Send card payments from the POS to a Linkly PIN pad. Pick which iPads use it.',
    turnOffWarning: null,
  },
  // Zest venues turn it on by subscription (`zestBillingSchema.ts`). It gates nothing in the apps:
  // while it's on, OrderHub sets up the venue's domain by hand.
  'custom-domain': {
    type: 'feature',
    requires: 'online-ordering',
    label: 'Custom domain',
    description: 'Take online orders on your own website address.',
    turnOffWarning: null,
  },
  'square-integration': {
    type: 'feature',
    requires: null,
    label: 'Square',
    description: 'Your online menu comes from Square, and online orders go to Square.',
    turnOffWarning: null,
  },
  // Turning it off deletes the venue's stock rows (`featureFlags/domain.saveFeatureFlags`)
  'menu-item-stock': {
    type: 'experimental',
    requires: 'pos',
    label: 'Stock counts',
    description: 'Count stock and mark items sold out on the POS. Sold out items show online.',
    turnOffWarning:
      "Deletes the venue's stock counts and sold out marks. Turning it back on won't bring them back.",
  },
  // The MCP server (`packages/backend/convex/mcp/README.md`)
  'mcp-server': {
    type: 'experimental',
    requires: null,
    label: 'MCP server',
    description:
      'Use Claude, ChatGPT or another AI agent to check the menu and online orders, and change menu items.',
    turnOffWarning: null,
  },
  // admin-native's hold to talk on every tab but Support: short conversations with the assistant
  // about the screen staff are on (`features/voiceChat/` in admin-native). The Support tab is
  // `support-chat`.
  'voice-chat': {
    type: 'experimental',
    requires: null,
    label: 'Voice chat',
    description:
      'Hold to talk to the AI assistant on any screen in the admin app, to make changes on that screen.',
    turnOffWarning: null,
  },
  // Customers order by phone from an AI agent on the venue's AI phone number
  // (`packages/backend/convex/aiPhone/README.md`). The number is set in `aiPhoneSettings`.
  'ai-phone-ordering': {
    type: 'experimental',
    requires: 'online-ordering',
    label: 'AI phone ordering',
    description:
      "Customers ring the venue's AI phone number to order pickup, and pay when they collect it.",
    turnOffWarning: null,
  },
} as const satisfies Record<
  FeatureFlagKey,
  {
    type: FeatureFlagType;
    requires: FeatureFlagKey | null;
    label: string;
    description: string;
    turnOffWarning: string | null;
  }
>;

/** The products: what a venue buys whole. */
type ProductKey = {
  [K in FeatureFlagKey]: (typeof FEATURE_FLAGS)[K]['type'] extends 'product' ? K : never;
}[FeatureFlagKey];

// Only a product can be required
assertTypes<
  Exclude<(typeof FEATURE_FLAGS)[FeatureFlagKey]['requires'], null> extends ProductKey
    ? true
    : false
>();

/** The flags that may go in a plan: products and features. */
export type PlanFeatureFlagKey = {
  [K in FeatureFlagKey]: (typeof FEATURE_FLAGS)[K]['type'] extends 'product' | 'feature'
    ? K
    : never;
}[FeatureFlagKey];

/** One venue's flag that is on. See the convention above. */
export const featureFlagSchema = z.object({
  restaurantId: z.string(),
  /** A `FeatureFlagKey`, stored as a plain string so a key a build doesn't know still reads. */
  key: z.string(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type FeatureFlag = z.output<typeof featureFlagSchema>;

/** Strict schema: only keys in the registry can be written. */
export const featureFlagStrictSchema = featureFlagSchema.safeExtend({
  key: zFeatureFlagKey,
});

/** Convex schema */
export const featureFlagCvxSchema = featureFlagSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type FeatureFlagCvx = z.output<typeof featureFlagCvxSchema>;
assertTypes<NoNewFields<FeatureFlag, FeatureFlagCvx>>();
